import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

const sb: any = supabase as any;

interface HistoryEvent {
  at: string;
  actor_role: string | null;
  actor_id: string | null;
  actor_name?: string | null;
  kind: string;
  summary: string;
  meta?: Record<string, unknown> | null;
}

const KIND_LABEL: Record<string, string> = {
  message: "Message",
  evidence_request: "Evidence Request",
  evidence_request_created: "Evidence Request",
  evidence_request_cancelled: "Request Cancelled",
  evidence_fulfilled: "Evidence Fulfilled",
  status_change: "Status Change",
  progress_update: "Progress Update",
  validation: "Reviewer Validation",
  validation_decision: "Reviewer Validation",
  reviewer_comment: "Reviewer Comment",
  owner_change: "Owners Updated",
  due_date_change: "Due Date Updated",
  system: "System",
};

export const FindingHistory: React.FC<{ findingId: string }> = ({ findingId }) => {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await sb.rpc("cap_get_history", { p_finding_id: findingId });
    if (!error) setEvents((data ?? []) as HistoryEvent[]);
    setLoading(false);
  }, [findingId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading history…
      </div>
    );
  }
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground italic">No history events yet.</p>;
  }
  return (
    <ol className="relative border-l border-border pl-4 space-y-3">
      {events.map((e, i) => (
        <li key={`${e.at}-${i}`} className="relative">
          <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <Badge variant="outline" className="text-[10px]">
              {KIND_LABEL[e.kind] ?? e.kind}
            </Badge>
            {e.actor_role && (
              <span className="text-[11px] text-muted-foreground">{e.actor_role}</span>
            )}
            {e.actor_name && (
              <span className="text-[11px] text-foreground font-medium">{e.actor_name}</span>
            )}
            <span className="text-[10px] text-muted-foreground">
              {new Date(e.at).toLocaleString()}
            </span>
          </div>
          <p className="text-xs text-foreground whitespace-pre-wrap">{e.summary}</p>
        </li>
      ))}
    </ol>
  );
};

export default FindingHistory;
